import { GetStaticProps } from 'next'
import ErrorPage from 'next/error'
import { useRouter } from 'next/router'
import { JobPostingJsonLd } from 'next-seo'
import React from 'react'
import Button from '#/components/Button'
import Link from '#/components/Link'
import Meta from '#/components/Meta/meta'
import { host } from '#/configs'
import PostTitle from '#/containers/Blog/components/post-title'
import Date from '#/containers/Careers/components/date'
import PostBody from '#/containers/Careers/components/post-body'
import PostHeader from '#/containers/Careers/components/post-header'
import {
  getAllPostsWithSlug,
  getPostAndMorePosts
} from '#/containers/Careers/lib/api'
import { logEvent } from '#/services/analytics'

type JobDetails = {
  job: Blog.Post
}

const Job: React.FC<JobDetails> = ({ job }) => {
  const router = useRouter()

  if (!router.isFallback && !job?.slug) {
    return <ErrorPage statusCode={404} />
  }

  function logApplyClick(position: string) {
    logEvent('select_content', {
      content_type: 'careers',
      item_id: 'apply',
      location: `${position}`
    })
  }

  const meta = {
    title: job?.title,
    openGraph: {
      opengraphTitle:
        "TopHap is a small team of developers and designers building tools to help real estate professionals research more effectively. Come join our team that's reinventing real estate research.",
      opengraphDescription:
        "TopHap is a small team of developers and designers building tools to help real estate professionals research more effectively. Come join our team that's reinventing real estate research."
    },
    image: `${host}/images/blog/tophap-blog.jpg`,
    site_name: 'TopHap | Careers'
  }

  return (
    <>
      <Meta meta={meta} />
      <JobPostingJsonLd
        applicantLocationRequirements='FR'
        baseSalary={{
          currency: 'US',
          value: [0, 120],
          unitText: 'MONTH'
        }}
        datePosted={job?.date}
        description={job?.excerpt + ''}
        employmentType={['FULL_TIME', 'CONTRACTOR', 'PART_TIME']}
        hiringOrganization={{
          name: 'TopHap',
          sameAs: 'www.tophap.com'
        }}
        jobLocation={{
          streetAddress: '360 Hartz Ave',
          addressLocality: 'Danville',
          addressRegion: 'California',
          postalCode: '94526',
          addressCountry: 'United States'
        }}
        title={job?.title}
        validThrough='2022-01-06'
      />

      {router.isFallback ? (
        <PostTitle>Loading…</PostTitle>
      ) : (
        <div className='mx-auto w-full max-w-6xl'>
          <main className='mx-auto max-w-2xl px-6 lg:max-w-6xl'>
            <article className='grid border-t-2 border-gray-100 pb-20 pt-12 lg:grid-cols-3 lg:gap-16'>
              <div className='hidden lg:block'>
                <aside className='sticky top-20 mb-64 rounded-lg bg-gray-50 p-8'>
                  <div className='-my-4 divide-y-2 divide-gray-100'>
                    <dl className='divide-y-2 divide-gray-100'>
                      <div className='py-4'>
                        <dt className='sr-only'>Employment type</dt>
                        <dl className='flex items-start space-x-2 text-sm leading-5'>
                          <svg
                            className='h-5 w-5 text-gray-400'
                            fill='currentColor'
                            viewBox='0 0 20 20'
                          >
                            <path
                              clipRule='evenodd'
                              d='M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z'
                              fillRule='evenodd'
                            />
                            <path d='M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15a24.98 24.98 0 01-8-1.308z' />
                          </svg>
                          <div className='text-gray-800'>
                            {job.tags.edges.map(
                              (tag: { node: { name: string } }) =>
                                (tag?.node.name === 'full-time' ?
                                  'Full time' :
                                  tag?.node.name === 'contract' ?
                                    'Contract' :
                                    tag?.node.name === 'part-time' ?
                                      'Part time' :
                                      '')
                            )}
                          </div>
                        </dl>
                      </div>
                      <div className='py-4'>
                        <dt className='sr-only'>Location</dt>
                        <dl className='flex items-start space-x-2 text-sm leading-5'>
                          <svg
                            className='h-5 w-5 text-gray-400'
                            fill='currentColor'
                            viewBox='0 0 20 20'
                          >
                            <path
                              clipRule='evenodd'
                              d='M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z'
                              fillRule='evenodd'
                            />
                          </svg>
                          <div>
                            <div className='text-gray-800'>
                              {job.tags.edges.map(
                                (tag: { node: { name: string } }) =>
                                  (tag?.node.name === 'remote' ?
                                    'Remote' :
                                    tag?.node.name === 'on-site' ?
                                      'On-site' :
                                      '')
                              )}
                            </div>
                            <div className='text-gray-500'>
                              Danville, CA, USA
                            </div>
                          </div>
                        </dl>
                      </div>
                      <div className='py-4'>
                        <dt className='sr-only'>Salary</dt>
                        <dl className='flex items-start space-x-2 text-sm leading-5'>
                          <svg
                            className='h-5 w-5 text-gray-400'
                            fill='currentColor'
                            viewBox='0 0 20 20'
                          >
                            <path d='M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z' />
                            <path
                              clipRule='evenodd'
                              d='M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z'
                              fillRule='evenodd'
                            />
                          </svg>
                          <div style={{ fontVariantNumeric: 'tabular-nums' }}>
                            <div className='text-gray-800'>DOE</div>
                          </div>
                        </dl>
                      </div>
                      <div className='py-4'>
                        <dt className='sr-only'>Opened date</dt>
                        <dl className='flex items-start space-x-2 text-sm leading-5'>
                          <svg
                            className='h-5 w-5 text-gray-400'
                            fill='currentColor'
                            viewBox='0 0 20 20'
                          >
                            <path
                              clipRule='evenodd'
                              d='M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z'
                              fillRule='evenodd'
                            />
                          </svg>
                          <span className='text-gray-800'>
                            <span aria-hidden='true'>Opened on</span>
                            {' '}
                            <Date dateString={job.date} />
                          </span>
                        </dl>
                      </div>
                    </dl>
                    <div className='flex items-center space-x-8 pt-6'>
                      <Link
                        href={`${router.asPath}/apply`}
                        onClick={() => logApplyClick('sider')}
                      >
                        <Button className='inline-block cursor-pointer rounded-md bg-gradient-to-r from-primary to-gradient-blue px-4 py-2 text-sm font-medium leading-6 text-white focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'>
                          Apply for this job
                        </Button>
                      </Link>
                    </div>
                  </div>
                </aside>
              </div>

              <div className='col-span-2'>
                {router.isFallback ? (
                  <PostTitle>Loading…</PostTitle>
                ) : (
                  <>
                    <PostHeader excerpt={job.excerpt} title={job?.title} />
                    <div className='flow-root pb-8 pt-3 lg:hidden'>
                      <div className='-my-4'>
                        <dl className='sm:grid sm:grid-cols-2'>
                          <div className='border-b border-gray-100 py-4'>
                            <dt className='sr-only'>Employment type</dt>
                            <dl className='flex items-start space-x-2 text-sm leading-5'>
                              <svg
                                className='h-5 w-5 text-gray-400'
                                fill='currentColor'
                                viewBox='0 0 20 20'
                              >
                                <path
                                  clipRule='evenodd'
                                  d='M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z'
                                  fillRule='evenodd'
                                />
                                <path d='M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15a24.98 24.98 0 01-8-1.308z' />
                              </svg>
                              <span className='text-gray-800'>
                                {job.tags.edges.map(
                                  (tag: { node: { name: string } }) =>
                                    (tag?.node.name === 'full-time' ?
                                      'Full-time' :
                                      tag?.node.name === 'contract' ?
                                        'Contract' :
                                        tag?.node.name === 'part-time' ?
                                          'Part-time' :
                                          '')
                                )}
                              </span>
                            </dl>
                          </div>
                          <div className='border-b border-gray-100 py-4'>
                            <dt className='sr-only'>Open date</dt>
                            <dl className='flex items-start space-x-2 text-sm leading-5'>
                              <svg
                                className='h-5 w-5 text-gray-400'
                                fill='currentColor'
                                viewBox='0 0 20 20'
                              >
                                <path
                                  clipRule='evenodd'
                                  d='M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z'
                                  fillRule='evenodd'
                                />
                              </svg>
                              <span className='text-gray-800'>
                                <span aria-hidden='true'>Opened on</span>
                                {' '}
                                <Date dateString={job.date} />
                              </span>
                            </dl>
                          </div>
                          <div className='border-b border-gray-100 py-4'>
                            <dt className='sr-only'>Location</dt>
                            <dl className='flex items-start space-x-2 text-sm leading-5'>
                              <svg
                                className='h-5 w-5 text-gray-400'
                                fill='currentColor'
                                viewBox='0 0 20 20'
                              >
                                <path
                                  clipRule='evenodd'
                                  d='M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z'
                                  fillRule='evenodd'
                                />
                              </svg>
                              <div>
                                <div className='text-gray-800'>
                                  {job.tags.edges.map(
                                    (tag: { node: { name: string } }) =>
                                      (tag?.node.name === 'remote' ?
                                        'Remote' :
                                        tag?.node.name === 'on-site' ?
                                          'On-site' :
                                          '')
                                  )}
                                </div>
                                <div className='text-gray-500'>
                                  Danville, CA, USA
                                </div>
                              </div>
                            </dl>
                          </div>
                          <div className='border-b border-gray-100 py-4'>
                            <dt className='sr-only'>Salary</dt>
                            <dl className='flex items-start space-x-2 text-sm leading-5'>
                              <svg
                                className='h-5 w-5 text-gray-400'
                                fill='currentColor'
                                viewBox='0 0 20 20'
                              >
                                <path d='M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z' />
                                <path
                                  clipRule='evenodd'
                                  d='M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z'
                                  fillRule='evenodd'
                                />
                              </svg>
                              <div
                                style={{ fontVariantNumeric: 'tabular-nums' }}
                              >
                                <div className='text-gray-800'>DOE </div>
                              </div>
                            </dl>
                          </div>
                        </dl>
                      </div>
                    </div>
                    <PostBody content={job.content} />
                  </>
                )}
              </div>
            </article>
          </main>
        </div>
      )}
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({
  params,
  preview = false
}) => {
  const data = await getPostAndMorePosts(params?.slug, preview)

  return {
    props: { job: data.job },
    revalidate: 1
  }
}

type Node = {
  node: {
    slug: string
  }
}

export async function getStaticPaths() {
  const allJobs = await getAllPostsWithSlug()

  return {
    paths: allJobs.edges.map(({ node }: Node) => `/careers/${node.slug}`),
    fallback: true
  }
}

export default Job
